import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Select,
  MenuItem,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  CssBaseline,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { bookmakerLogos } from '../assets/bookmakerLogos';

// Static list of bookmakers in specified order
const staticBookmakers = [
  'DraftKings',
  'Pinnacle',
  'Bookmaker',
  'FanDuel',
  'ESPN BET',
  'Caesars',
  'BetMGM',
  'BetRivers',  
  'Builder',
  'Bet365',
  'Bovada',
  'Fliff',
  'Hard Rock',
  'Prophet Exchange',
  'NoVig',
];

// Custom dark theme
const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: { fontFamily: 'Roboto, Arial, sans-serif', h5: { fontWeight: 600 } },
  components: {
    MuiInputLabel: { styleOverrides: { root: { color: '#ffffff' } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
        },
      },
    },
    MuiMenu: { styleOverrides: { paper: { backgroundColor: '#333333', color: '#ffffff' } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: { root: { color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } } },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: '#121212',
        },
        indicator: {
          backgroundColor: '#ff1744',
          height: '2px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#121212',
          borderRadius: '8px 8px 0 0',
          transition: 'background-color 0.2s, color 0.2s',
          '&.Mui-selected': {
            color: '#ffffff',
            backgroundColor: '#121212',
          },
          '&:hover': {
            backgroundColor: '#1a1a1a',
          },
        },
      },
    },
  },
});

const Props = ({ bets }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const apiRef = useRef(null);
  
  const [sport, setSport] = useState('NFL');
  const [betType, setBetType] = useState('Passing Yards');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);
  const [selectedMatchup, setSelectedMatchup] = useState('All');
  
  // Add state for selected books
  const [selectedBooks, setSelectedBooks] = useState(() => {
    const saved = localStorage.getItem(`propsSelectedBooks_${isMobile ? 'mobile' : 'desktop'}`);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error loading selected books:', e);
      }
    }
    // Default to all books selected except Prophet Exchange and NoVig
    return staticBookmakers.filter(book => book !== 'Prophet Exchange' && book !== 'NoVig');
  });

  // Save selected books when they change
  useEffect(() => {
    localStorage.setItem(`propsSelectedBooks_${isMobile ? 'mobile' : 'desktop'}`, JSON.stringify(selectedBooks));
  }, [selectedBooks, isMobile]);

  // Reset selectedMatchup to 'All' when sport changes
  useEffect(() => {
    setSelectedMatchup('All');
  }, [sport]);

  // Dynamically generate bet types and matchups from available data
  const { betTypes, matchups } = useMemo(() => {
    if (!Array.isArray(bets)) {
      console.log('Bets data is not an array:', bets);
      return { betTypes: [], matchups: [] };
    }
    const types = new Set();
    const matchupData = new Map();

    bets.forEach(bet => {
      if (bet && bet.betCategory === 'Prop' && bet.leagueName === sport) {
        if (bet.betTypeName) types.add(bet.betTypeName);
        if (bet.eventShortName && bet.eventStart) {
          const currentData = matchupData.get(bet.eventShortName);
          const betTime = new Date(bet.eventStart).getTime();
          if (!currentData || betTime < currentData.eventStart) {
            matchupData.set(bet.eventShortName, {
              eventStart: betTime,
              awayLogo: bet.awayLogo || '',
              homeLogo: bet.homeLogo || '',
            });
          }
        }
      }
    });
    const sortedTypes = Array.from(types).sort();
    const sortedMatchups = Array.from(matchupData.entries())
      .sort((a, b) => {
        const timeA = a[1].eventStart && !isNaN(a[1].eventStart) ? a[1].eventStart : Number.MAX_SAFE_INTEGER;
        const timeB = b[1].eventStart && !isNaN(b[1].eventStart) ? b[1].eventStart : Number.MAX_SAFE_INTEGER;
        return timeA - timeB;
      })
      .map(entry => ({
        eventShortName: entry[0],
        awayLogo: entry[1].awayLogo,
        homeLogo: entry[1].homeLogo,
      }));
    return { betTypes: sortedTypes, matchups: sortedMatchups };
  }, [bets, sport]);

  // Set initial betType when betTypes change
  useEffect(() => {
    if (betTypes.length > 0 && (!betType || !betTypes.includes(betType))) {
      // Sport-specific defaults
      if (sport === 'NFL' && betTypes.includes('Passing Yards')) {
        setBetType('Passing Yards');
      } else if (sport === 'MLB' && betTypes.includes('Pitcher Strikeouts')) {
        setBetType('Pitcher Strikeouts');
      } else if (sport === 'CFB' && betTypes.includes('Passing Yards')) {
        setBetType('Passing Yards');
      } else {
        setBetType(betTypes[0]);
      }
    } else if (betTypes.length === 0) {
      setBetType('');
    }
  }, [betTypes, betType, sport]);

  // Handle books filter change
  const handleBooksFilterChange = (event) => {
    const value = event.target.value;
    // Handle "Select All" option
    if (value[value.length - 1] === 'all') {
      setSelectedBooks(selectedBooks.length === staticBookmakers.length ? [] : staticBookmakers);
    } else {
      setSelectedBooks(value);
    }
  };

  // Render value for books dropdown
  const renderBooksValue = (selected) => {
    if (!selected.length) return 'None';
    if (selected.length === staticBookmakers.length) return 'All';
    if (selected.length > 1) return `Multiple (${selected.length})`;
    return selected[0];
  };

  // Group bets into a single table
  const tableData = useMemo(() => {
    if (!Array.isArray(bets)) {
      console.log('Bets data is not an array:', bets);
      return { rows: [], columns: [] };
    }

    const filteredBets = bets.filter(bet => {
      if (!bet) return false;
      return (
        bet.betCategory === 'Prop' &&
        bet.leagueName === sport &&
        bet.betTypeName === betType
      );
    });

    console.log('Filtered prop bets for PropExplorer table:', filteredBets.length, 'items', filteredBets);

    const eventPlayerGroups = {};
    filteredBets.forEach(bet => {
      const eventKey = `${bet.eventName}-${bet.playerName || ''}`;
      if (!eventPlayerGroups[eventKey]) {
        eventPlayerGroups[eventKey] = {
          event: bet.eventName,
          player: bet.playerName || '',
          position: bet.position || '',
          eventShortName: bet.eventShortName || '',
          startTime: bet.eventStart,
          awayLogo: bet.awayLogo || '',
          homeLogo: bet.homeLogo || '',
          bets: [],
        };
      }
      eventPlayerGroups[eventKey].bets.push(bet);
    });

    const rowsWithStartTime = [];
    let rowId = 0;

    Object.entries(eventPlayerGroups).forEach(([eventKey, group]) => {
      const row = {
        id: rowId++,
        eventName: group.event,
        playerName: group.player,
        position: group.position,
        displayName: group.position ? `${group.player}` : group.player,
        eventShortName: group.eventShortName,
        awayLogo: group.awayLogo,
        homeLogo: group.homeLogo,
        eventStart: group.startTime,
        eventStartFormatted: {
          date: group.startTime
            ? new Date(group.startTime).toLocaleDateString('en-US', {
                year: '2-digit',
                month: 'numeric',
                day: 'numeric',
              })
            : '',
          time: group.startTime
            ? new Date(group.startTime).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              })
            : '',
        },
        bookmakerOdds: {},
        bestOddsBookmakers: { Over: '', Under: '' },
      };

      // Filter by selected matchup
      if (selectedMatchup !== 'All' && row.eventShortName !== selectedMatchup) {
        return;
      }

      const oddsBets = group.bets.filter(bet => bet.betTypeName === betType);

      const outcomes = { Over: {}, Under: {} };
      const outcomeDetails = { Over: {}, Under: {} };
      oddsBets.forEach(bet => {
        const outcome = bet.side === 'Over' ? 'Over' : 'Under';
        outcomes[outcome][bet.marketSourceName] = bet.price;
        outcomeDetails[outcome][`${bet.marketSourceName}Points`] = bet.points;
        outcomeDetails[outcome][`${bet.marketSourceName}Logo`] = bookmakerLogos[bet.marketSourceName] || '';
        outcomeDetails[outcome][`${bet.marketSourceName}OutcomeName`] = bet.side || outcome;
        outcomeDetails[outcome][`${bet.marketSourceName}LineDisplay`] = `${outcome === 'Over' ? 'o' : 'u'}${bet.points || ''}`;
      });

      // Calculate best odds only from selected books
      const bestOdds = {};
      const bestOddsKeys = ['Over', 'Under'];
      bestOddsKeys.forEach(outcome => {
        const entries = [];
        // Only consider selected books for best odds
        selectedBooks.forEach(book => {
          const price = outcomes[outcome][book] ? parseFloat(outcomes[outcome][book]) : null;
          const points = outcomeDetails[outcome][`${book}Points`];
          if (price !== null) {
            entries.push({
              book,
              price,
              points: points !== undefined ? parseFloat(points) : null,
              logo: outcomeDetails[outcome][`${book}Logo`],
              outcomeName: outcomeDetails[outcome][`${book}OutcomeName`],
              lineDisplay: outcomeDetails[outcome][`${book}LineDisplay`],
            });
          }
        });

        let bestEntry = null;
        if (entries.length > 0) {
          entries.sort((a, b) => {
            if (a.points !== b.points) {
              return outcome === 'Over' ? a.points - b.points : b.points - a.points;
            }
            return b.price - a.price;
          });
          bestEntry = entries[0];
        }

        if (bestEntry) {
          bestOdds[outcome] = {
            price: bestEntry.price,
            points: bestEntry.points,
            logo: bestEntry.logo,
            outcomeName: bestEntry.outcomeName,
            lineDisplay: bestEntry.lineDisplay,
          };
          row.bestOddsBookmakers[outcome] = bestEntry.book;
        } else {
          bestOdds[outcome] = {
            price: null,
            points: null,
            logo: '',
            outcomeName: outcome,
            lineDisplay: '',
          };
          row.bestOddsBookmakers[outcome] = '';
        }
      });

      staticBookmakers.forEach(book => {
        const odds = {};
        bestOddsKeys.forEach(outcome => {
          const price = outcomes[outcome][book] ? parseFloat(outcomes[outcome][book]) : null;
          if (price !== null) {
            const formattedPrice = price > 0 ? `+${price}` : `${price}`;
            const line = outcomeDetails[outcome][`${book}LineDisplay`] || '';
            odds[outcome] = line ? `${line} ${formattedPrice}` : formattedPrice;
          } else {
            odds[outcome] = '';
          }
        });
        row.bookmakerOdds[book] = odds;
        row[book] = '';
      });

      bestOddsKeys.forEach(outcome => {
        const { price, lineDisplay } = bestOdds[outcome] || {};
        if (price !== null) {
          const formattedPrice = price > 0 ? `+${price}` : `${price}`;
          bestOdds[outcome].display = lineDisplay ? `${lineDisplay} ${formattedPrice}` : formattedPrice;
        } else {
          bestOdds[outcome].display = '';
        }
      });

      row.bestOdds = bestOdds;
      rowsWithStartTime.push(row);
    });

    rowsWithStartTime.sort((a, b) => {
      const dateA = a.eventStart && !isNaN(new Date(a.eventStart).getTime()) ? new Date(a.eventStart) : new Date(9999, 0, 1);
      const dateB = b.eventStart && !isNaN(new Date(b.eventStart).getTime()) ? new Date(b.eventStart) : new Date(9999, 0, 1);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA - dateB;
      }
      const playerA = a.playerName || '';
      const playerB = b.playerName || '';
      return playerA.localeCompare(playerB);
    });

    rowsWithStartTime.forEach(row => {
      if (!row.eventStart || isNaN(new Date(row.eventStart).getTime())) {
        console.log('Invalid eventStart for row:', row);
      }
    });

    return { rows: rowsWithStartTime, columns: [] };
  }, [bets, sport, betType, selectedMatchup, selectedBooks]);

  // Get saved column order for current context
  const getSavedColumnOrder = () => {
    try {
      const key = `propsColumnOrder_${sport}_${betType}_${selectedMatchup}_${isMobile ? 'mobile' : 'desktop'}`;
      const saved = localStorage.getItem(key);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading column order:', error);
    }
    return null;
  };

  // Save column order for current context
  const saveColumnOrder = (order) => {
    try {
      const key = `propsColumnOrder_${sport}_${betType}_${selectedMatchup}_${isMobile ? 'mobile' : 'desktop'}`;
      localStorage.setItem(key, JSON.stringify(order));
    } catch (error) {
      console.error('Error saving column order:', error);
    }
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: 'eventName',
        headerName: '',
        minWidth: isMobile ? 135 : 165,
        flex: isMobile ? 1 : 1,
        sortable: false,
        disableReorder: true,
        renderCell: (params) => {
          const [awayTeam, homeTeam] = params.row.eventShortName ? params.row.eventShortName.split('@') : ['', ''];
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'left', gap: '0', justifyContent: 'center', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', height: '35px', paddingLeft: isMobile ? '4px' : '8px', paddingRight: isMobile ? '0px' : '0' }}>
                <Typography
                  sx={{
                    fontSize: isMobile ? '12px' : '14px',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                  }}
                >
                  {params.row.displayName}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', height: '35px', paddingLeft: isMobile ? '4px' : '8px', paddingRight: isMobile ? '0px' : '0' }}>
                {params.row.awayLogo && (
                  <img
                    src={params.row.awayLogo}
                    alt="Away Team Logo"
                    style={{ width: isMobile ? '16px' : '24px', height: isMobile ? '16px' : '24px', objectFit: 'contain', marginRight: '2px' }}
                  />
                )}
                <Typography sx={{ fontSize: isMobile ? '11px' : '14px' }}>{awayTeam}</Typography>
                <Typography sx={{ fontSize: isMobile ? '10px' : '12px', mx: '2px' }}>@</Typography>
                {params.row.homeLogo && (
                  <img
                    src={params.row.homeLogo}
                    alt="Home Team Logo"
                    style={{ width: isMobile ? '16px' : '24px', height: isMobile ? '16px' : '24px', objectFit: 'contain', marginRight: '2px' }}
                  />
                )}
                <Typography sx={{ fontSize: isMobile ? '11px' : '14px' }}>{homeTeam}</Typography>
              </Box>
            </Box>
          );
        },
      },
      {
        field: 'eventStartFormatted',
        headerName: '',
        minWidth: isMobile ? 70 : 85,
        maxWidth: isMobile ? 70 : 85,
        sortable: false,
        disableReorder: true,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'left', gap: '0', justifyContent: 'center', height: '100%' }}>
            <Box sx={{ height: '35px', display: 'flex', alignItems: 'center', paddingLeft: isMobile ? '0px' : '8px', paddingRight: isMobile ? '4px' : '8px' }}>
              <Typography sx={{ fontSize: isMobile ? '12px' : '14px' }}>{params.value.date}</Typography>
            </Box>
            <Box sx={{ height: '35px', display: 'flex', alignItems: 'center', paddingLeft: isMobile ? '0px' : '8px', paddingRight: isMobile ? '4px' : '8px' }}>
              <Typography sx={{ fontSize: isMobile ? '12px' : '14px' }}>{params.value.time}</Typography>
            </Box>
          </Box>
        ),
      },
      {
        field: 'bestOdds',
        headerName: 'Best Odds',
        headerAlign: 'right',
        minWidth: isMobile ? 110 : 125,
        flex: 1,
        sortable: false,
        disableReorder: true,
        renderHeader: () => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', paddingRight: isMobile ? '4px' : '8px' }}>
            <Typography sx={{ fontSize: isMobile ? '14px' : '16px', color: '#ffffff' }}>Best Odds</Typography>
          </Box>
        ),
        renderCell: (params) => {
          const value = params.value;
          const outcomes = ['Over', 'Under'];
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0', justifyContent: 'center', height: '100%' }}>
              {outcomes.map(outcome => {
                const { display, logo, outcomeName } = value[outcome] || {};
                if (!display) return null;
                return (
                  <Box key={outcome} sx={{ display: 'flex', alignItems: 'center', height: '35px', paddingRight: '8px' }}>
                    <Typography
                      sx={{
                        fontSize: isMobile ? '12px' : '14px',
                        textAlign: 'right',
                        pr: isMobile ? '4px' : '8px',
                        flex: 1,
                      }}
                    >
                      {display}
                    </Typography>
                    {logo && (
                      <img
                        src={logo}
                        alt={`${outcomeName} Logo`}
                        style={{ width: '30px', height: '30px', objectFit: 'contain' }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          );
        },
      },
    ];

    // Only show selected bookmakers
    const visibleBookmakers = selectedBooks;
    
    // Create bookmaker columns
    const bookmakerColumns = {};
    visibleBookmakers.forEach(book => {
      bookmakerColumns[book] = {
        field: book,
        headerName: '',
        headerAlign: 'right',
        minWidth: isMobile ? 90 : 100,
        flex: 1,
        sortable: false,
        disableReorder: false,
        renderHeader: () => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', paddingRight: isMobile ? '4px' : '12px' }}>
            {bookmakerLogos[book] ? (
              <img
                src={bookmakerLogos[book]}
                alt={book}
                style={{ width: isMobile ? '50px' : '70px', height: 'auto', objectFit: 'contain' }}
              />
            ) : (
              <Box sx={{ width: isMobile ? '50px' : '70px', height: 'auto' }} />
            )}
          </Box>
        ),
        renderCell: (params) => {
          const row = params.row;
          const bookOdds = row.bookmakerOdds && row.bookmakerOdds[params.field];
          const bestBookmaker = row.bestOddsBookmakers;
          if (bookOdds) {
            const outcomes = ['Over', 'Under'];
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0', justifyContent: 'center', height: '100%' }}>
                {outcomes.map(outcome => (
                  <Box
                    key={outcome}
                    sx={{
                      height: '35px',
                      display: 'flex',
                      alignItems: 'center',
                      border: params.field === bestBookmaker[outcome] ? '2px solid #00cc00' : 'none',
                      borderRadius: '0',
                      width: '100%',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? '12px' : '14px',
                        textAlign: 'right',
                        pr: isMobile ? '4px' : '12px',
                        flex: 1,
                      }}
                    >
                      {bookOdds[outcome] || ''}
                    </Typography>
                  </Box>
                ))}
              </Box>
            );
          }
          return <Box sx={{ textAlign: 'right', pr: '12px' }}><Typography sx={{ fontSize: '14px' }}></Typography></Box>;
        },
      };
    });

    // Get saved order or use default
    const savedOrder = getSavedColumnOrder();
    const finalColumns = [...baseColumns];
    
    if (savedOrder && savedOrder.bookmakers) {
      // Add bookmaker columns in saved order, but only if they're selected
      savedOrder.bookmakers.forEach(book => {
        if (bookmakerColumns[book]) {
          finalColumns.push(bookmakerColumns[book]);
        }
      });
      
      // Add any new bookmaker columns not in saved order
      visibleBookmakers.forEach(book => {
        if (!savedOrder.bookmakers.includes(book) && bookmakerColumns[book]) {
          finalColumns.push(bookmakerColumns[book]);
        }
      });
    } else {
      // No saved order, use default
      visibleBookmakers.forEach(book => {
        finalColumns.push(bookmakerColumns[book]);
      });
    }

    return finalColumns;
  }, [sport, betType, selectedMatchup, isMobile, selectedBooks]);

  // Handle column reordering
  const handleColumnOrderChange = (params) => {
    if (apiRef.current) {
      const state = apiRef.current.state;
      const orderedFields = state.columns.orderedFields || state.columns.all;
      
      if (orderedFields) {
        // Extract bookmaker columns
        const fixedColumns = ['eventName', 'eventStartFormatted', 'bestOdds'];
        const bookmakers = orderedFields.filter(field => !fixedColumns.includes(field));
        
        // Save the new order
        saveColumnOrder({
          fixed: fixedColumns,
          bookmakers: bookmakers
        });
      }
    }
  };

  // Restore column order when grid is ready
  useEffect(() => {
    if (apiRef.current && columns.length > 0) {
      const savedOrder = getSavedColumnOrder();
      if (savedOrder && savedOrder.bookmakers) {
        const fullOrder = [...savedOrder.fixed, ...savedOrder.bookmakers.filter(book => selectedBooks.includes(book))];
        
        // Use setTimeout to ensure grid is fully initialized
        setTimeout(() => {
          try {
            if (apiRef.current && apiRef.current.setColumnOrder) {
              apiRef.current.setColumnOrder(fullOrder);
            }
          } catch (error) {
            console.error('Error restoring column order:', error);
          }
        }, 100);
      }
    }
  }, [sport, betType, selectedMatchup, isMobile, columns, selectedBooks]);

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ width: '100%', height: '100vh', backgroundColor: 'background.default', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box
            className="filter-section"
            sx={{
              padding: isMobile ? '8px' : '16px',
              position: 'sticky',
              top: 0,
              zIndex: 3,
              backgroundColor: 'background.default',
            }}
          >
            <Stack 
              direction={isMobile ? "column" : "row"} 
              spacing={isMobile ? 1 : 2} 
              alignItems={isMobile ? "stretch" : "center"}
            >
              <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? '100%' : 120 }}>
                <InputLabel id="sport-filter-label" shrink sx={{ color: '#ffffff' }}>League</InputLabel>
                <Select
                  labelId="sport-filter-label"
                  label="League"
                  notched
                  value={sport}
                  onChange={(e) => setSport(e.target.value)}
                >
                  <MenuItem value="MLB">MLB</MenuItem>
                  <MenuItem value="NBA">NBA</MenuItem>
                  <MenuItem value="WNBA">WNBA</MenuItem>
                  <MenuItem value="NFL">NFL</MenuItem>
                  <MenuItem value="CFB">CFB</MenuItem>
                  <MenuItem value="NHL">NHL</MenuItem>
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? '100%' : 120 }}>
                <InputLabel id="bet-type-filter-label" shrink sx={{ color: '#ffffff' }}>Bet Type</InputLabel>
                <Select
                  labelId="bet-type-filter-label"
                  label="Bet Type"
                  notched
                  value={betType}
                  onChange={(e) => setBetType(e.target.value)}
                  disabled={betTypes.length === 0}
                >
                  {betTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? '100%' : 140 }}>
                <InputLabel id="books-filter-label" shrink sx={{ color: '#ffffff' }}>Books</InputLabel>
                <Select
                  multiple
                  labelId="books-filter-label"
                  label="Books"
                  notched
                  value={selectedBooks}
                  onChange={handleBooksFilterChange}
                  renderValue={renderBooksValue}
                >
                  <MenuItem value="all">
                    <Checkbox checked={selectedBooks.length === staticBookmakers.length} />
                    <ListItemText primary="Select All" />
                  </MenuItem>
                  {staticBookmakers.map((book) => (
                    <MenuItem key={book} value={book}>
                      <Checkbox checked={selectedBooks.includes(book)} />
                      <ListItemText primary={book} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <Box sx={{ backgroundColor: 'background.default', marginTop: isMobile ? '8px' : '0' }}>
              <Tabs
                value={selectedMatchup}
                onChange={(e, newValue) => setSelectedMatchup(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ minHeight: '32px' }}
              >
                <Tab label="All" value="All" sx={{ minHeight: '32px', padding: '6px 12px', fontSize: isMobile ? '12px' : '14px' }} />
                {matchups.map(matchup => (
                  <Tab
                    key={matchup.eventShortName}
                    value={matchup.eventShortName}
                    sx={{ minHeight: '32px', padding: '6px 12px', fontSize: isMobile ? '12px' : '14px' }}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {matchup.awayLogo && (
                          <img
                            src={matchup.awayLogo}
                            alt="Away Team Logo"
                            style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                          />
                        )}
                        <Typography sx={{ fontSize: isMobile ? '12px' : '14px' }}>@</Typography>
                        {matchup.homeLogo && (
                          <img
                            src={matchup.homeLogo}
                            alt="Home Team Logo"
                            style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                          />
                        )}
                      </Box>
                    }
                  />
                ))}
              </Tabs>
            </Box>
          </Box>
          <Box
            className="table-section"
            sx={{
              height: isMobile ? 'calc(100vh - 160px) !important' : 'calc(100vh - 127px) !important',
              overflowY: 'auto',
            }}
          >
            <Box sx={{ width: '100%', height: '100%' }}>
              {tableData.rows.length === 0 ? (
                <Box className="empty-state" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body1">
                    No prop bets available for the selected filters.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ height: '100%', width: '100%' }}>
                  <DataGridPro
                    apiRef={apiRef}
                    rows={tableData.rows}
                    columns={columns}
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    rowsPerPageOptions={[10, 25, 50]}
                    pagination
                    page={page}
                    onPageChange={(newPage) => setPage(newPage)}
                    disableSelectionOnClick
                    disableRowSelectionOnClick
                    disableColumnSort
                    disableColumnMenu
                    rowHeight={70}
                    onColumnOrderChange={handleColumnOrderChange}
                    sx={{
                      '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: '#1e1e1e',
                        borderBottom: '2px solid #444',
                        position: 'sticky',
                        top: '0',
                        zIndex: 2,
                      },
                      '& .MuiDataGrid-columnHeader': {
                        userSelect: 'none',
                        cursor: 'move',
                      },
                      '& .MuiDataGrid-columnHeader--alignRight': {
                        justifyContent: 'flex-end !important',
                      },
                      '& .MuiDataGrid-cell': {
                        color: '#ffffff !important',
                        userSelect: 'none',
                        padding: 0,
                        borderRight: '1px solid rgba(255, 255, 255, 0.3)',
                      },
                      '& .MuiDataGrid-cell:last-child': {
                        borderRight: 'none',
                      },
                      '& .MuiDataGrid-cell:focus': {
                        outline: 'none',
                      },
                      '& .MuiDataGrid-cell:focus-within': {
                        outline: 'none',
                      },
                      '& .MuiDataGrid-row:hover': { backgroundColor: '#1a1a1a' },
                      '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#1a1a1a', '&:hover': { backgroundColor: '#1a1a1a' } },
                      '& .MuiDataGrid-columnSeparator': { 
                        visibility: 'visible',
                        color: 'rgba(255, 255, 255, 0.3)',
                      },
                      '& .MuiDataGrid-virtualScroller': {
                        overflowY: 'auto !important',
                      },
                      '& .MuiDataGrid-root': {
                        overflowY: 'auto !important',
                        margin: 0,
                      },
                      '& .MuiDataGrid-main': {
                        padding: 0,
                      },
                      '& .MuiDataGrid-footerContainer': {
                        backgroundColor: '#1e1e1e',
                        color: '#ffffff !important',
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 1,
                      },
                      '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                        color: '#ffffff !important',
                      },
                      '& .MuiSelect-icon': { color: '#ffffff !important' },
                      '& .MuiDataGrid-menuIconButton': { color: '#ffffff !important' },
                      '& .MuiDataGrid-detailPanelToggleCell': { color: '#ffffff !important' },
                      '& .MuiDataGrid-columnHeaderTitle': {
                        color: '#ffffff !important',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        lineHeight: 1.2,
                      },
                      '& .MuiDataGrid-columnHeaderDraggableContainer': {
                        cursor: 'move',
                      },
                    }}
                    rowBuffer={5}
                    rowThreshold={2}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

export default Props;